import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import NavSearch from "@/components/NavSearch";

const links = [
  { to: "/", label: "Home" },
  { to: "/lessons", label: "Lessons" },
  { to: "/news", label: "News" },
  { to: "/prompts", label: "Prompt Library" },
  { to: "/blog", label: "Blog" },
  { to: "/workshops", label: "About" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 14);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled || open
          ? "bg-[#02080A]/90 backdrop-blur-2xl border-b border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
          : "bg-[#02080A]/40 backdrop-blur-md border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 group shrink-0" data-testid="nav-logo">
          <img src="/logo.png?v=3" alt="PromptsHouseBD" className="h-9 w-9 rounded-xl" />
          <span className="font-display font-bold text-white tracking-tight text-[15px] hidden sm:inline">
            Prompts<span className="text-gradient">HouseBD</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `px-3 py-2 text-[13px] font-medium rounded-lg transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-orange-300 bg-orange-500/10"
                    : "text-slate-200 hover:text-white hover:bg-white/10"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 flex-1 lg:flex-none justify-end">
          <NavSearch className="hidden md:block w-44 xl:w-60" />

          <div className="hidden md:flex items-center gap-2">
            {user && (
              <>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    className="px-3.5 py-2 rounded-lg text-sm text-orange-300 bg-orange-500/10 border border-orange-400/30 hover:bg-orange-500/20 transition-colors font-semibold whitespace-nowrap"
                    data-testid="nav-admin"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  className="px-3.5 py-2 rounded-lg text-sm border border-white/20 text-slate-200 font-medium hover:bg-white/5 transition-colors whitespace-nowrap"
                  data-testid="nav-dashboard"
                >
                  Dashboard
                </Link>
                <button
                  onClick={() => { logout(); navigate("/"); }}
                  className="text-sm text-slate-200 hover:text-white px-1 transition-colors"
                  data-testid="nav-logout"
                >
                  Logout
                </button>
              </>
            )}
          </div>

          <button
            className="lg:hidden text-[#04121A] border border-white/20 bg-white p-2.5 rounded-lg shrink-0"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mx-4 mt-1 mb-4 bg-[#0B191E]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-5 space-y-1">
          <NavSearch className="md:hidden mb-3" onNavigate={() => setOpen(false)} />
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block text-base text-slate-200 py-2 px-2 rounded-lg hover:bg-white/5"
              data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {l.label}
            </Link>
          ))}
          {user && (
            <div className="pt-3 border-t border-white/10 space-y-2">
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} className="block text-sm py-1 px-2 text-orange-300 font-semibold">Admin</Link>
              )}
              <Link to="/dashboard" onClick={() => setOpen(false)} className="block text-sm py-1 px-2 text-slate-200">Dashboard</Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-sm text-slate-200 px-2">Logout</button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
